import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { Page } from 'components/Page';
import { Txt } from 'components/Txt';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { theme } from 'lib/theme';
import { isEqual } from 'lodash';
import React from 'react';
import DraggableFlatList, {
  RenderItemParams,
} from 'react-native-draggable-flatlist';
import { useDispatch } from 'react-redux';
import {
  eventReorderScreenClose,
  eventReorderScreenDone,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';

export const ReorderScreen: React.FC<
  ModalScreenProps<ModalScreens.REORDER>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();

  const {
    options,
    title,
    subtitle,
    description,
    action,
    eventKey,
    closeOnDone = true,
  } = route.params;

  const initOrder = React.useRef(options.map((o) => o.value));
  const [order, setOrder] = React.useState(initOrder.current);

  const checkIsDirty = () => !isEqual(initOrder.current, order);

  const renderRow = React.useCallback(
    ({ drag, item, isActive }: RenderItemParams<(typeof order)[number]>) => {
      const option = options.find((o) => o.value === item)!;
      return (
        <Card separator={null} margin={false} marginBottomQuarter>
          <CardRow
            label={option.name}
            labelStyle={option.valueStyle}
            key={option.value}
            icon={option.icon}
            iconColor={option.iconColor}
            onDrag={drag}
            style={
              isActive ? { backgroundColor: theme.colors.blue10 } : undefined
            }
          />
        </Card>
      );
    },
    [options],
  );

  const handleDragEnd = React.useCallback(
    ({ data: newOrder }: { data: typeof order }) => setOrder(newOrder),
    [],
  );

  const handleClose = () => {
    if (checkIsDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleSave = () => {
    if (action) {
      dispatch({
        ...action,
        // @ts-expect-error payload does exist
        payload: { ...(action.payload || {}), order },
      });
      // The action should dispatch NAVIGATE_MODAL_BACK itself
      // This allows for async with failures
      return;
    }
    if (eventKey) {
      dispatch(
        eventReorderScreenDone({
          key: eventKey,
          numberValues: order.filter((v) => typeof v === 'number') as number[],
          stringValues: order.filter((v) => typeof v === 'string') as string[],
        }),
      );
    }
    if (closeOnDone) {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  };

  const isDirtyVar = checkIsDirty();
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  React.useEffect(() => {
    return () => {
      if (eventKey) {
        dispatch(eventReorderScreenClose({ key: eventKey }));
      }
    };
  }, [dispatch, eventKey]);

  return (
    <Page
      contentPaddingBottom={0}
      contentPaddingTop={theme.paddingPageVertical}
      header={
        <NavigationHeader
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
          saveButtonText="Done"
          heading={title || 'Reorder'}
          subheading={subtitle}
        />
      }
    >
      {!!description && (
        <CardGeneric style={{ marginBottom: theme.paddingPageVertical }}>
          <Txt>{description}</Txt>
        </CardGeneric>
      )}
      <DraggableFlatList
        // Required for web to drag
        activationDistance={1}
        data={order}
        renderItem={renderRow}
        keyExtractor={(item) => `${item}`}
        onDragEnd={handleDragEnd}
        ItemSeparatorComponent={() => null}
        containerStyle={{ flex: 1 }}
        scrollEnabled
      />
    </Page>
  );
};
